import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { playCelebration, shootCoins, playCoins, playExplosion } from "@/lib/confetti";
import CoinStreakBanner from "@/components/CoinStreakBanner";

function animateNumber(setter, from, to, duration) {
  const start = performance.now();
  const step = (now) => {
    const p = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - p, 3);
    setter(Math.round(from + (to - from) * eased));
    if (p < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

// Full coin-reward sequence played when a learner passes a quiz.
// Wahoo -> coins shoot up (cling) -> big number counts up -> coins fly to the
// grand total -> total balloons + explodes -> settles -> message + Next button.
export default function CoinCelebration({
  coinsAwarded, totalCoins, scorePercent, correctCount, totalCount,
  canAdvance, onRetry, onContinue, soundOn = true, hasPrev = false, onPrev, onDone,
}) {
  const prevTotal = Math.max(0, (totalCoins || 0) - (coinsAwarded || 0));
  const [count, setCount] = useState(0);
  const [total, setTotal] = useState(prevTotal);
  const [phase, setPhase] = useState("intro"); // intro -> coins -> total -> done
  const timers = useRef([]);
  const rootRef = useRef(null);
  const doneRef = useRef(null);

  useEffect(() => {
    const add = (fn, ms) => timers.current.push(setTimeout(fn, ms));
    if (soundOn) playCelebration(); // Wahoo!

    add(() => {
      setPhase("coins");
      if (soundOn) { shootCoins(); playCoins(11); }
      animateNumber(setCount, 0, coinsAwarded, 1200);
    }, 750);

    add(() => {
      setPhase("total");
      animateNumber(setTotal, prevTotal, totalCoins, 1000);
    }, 2150);

    add(() => { if (soundOn) playExplosion(); }, 3050);
    add(() => {
      setPhase("done");
      onDone && onDone();
    }, 3350);

    return () => timers.current.forEach(clearTimeout);
    // eslint-disable-next-line
  }, []);

  return (
    <div ref={rootRef} className="text-center py-2 relative overflow-hidden scroll-mt-24" data-testid="coin-celebration">
      {/* Row 1: coins earned + running total — stacked so number growth never reflows */}
      <div className="flex flex-col items-center gap-1.5 mb-1">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          data-testid="quiz-xp-awarded">
          <span className="text-3xl">🪙</span>
          <span className="text-2xl sm:text-3xl leading-none">+{count.toLocaleString()} coins today</span>
        </div>
        <motion.div
          className="inline-flex items-center gap-2 px-4 py-1.5 rounded-2xl font-extrabold font-display"
          style={{ background: "linear-gradient(135deg,#FFD600,#FF9E00)", color: "#3d2c00", boxShadow: "0 8px 24px rgba(255,158,0,0.45)" }}
          animate={phase === "total" ? { scale: [1, 1.9, 1] } : { scale: 1 }}
          transition={{ duration: 1.2, times: [0, 0.7, 1], ease: "easeOut" }}
          data-testid="coin-grand-total"
        >
          <span className="text-3xl">🪙</span>
          <span className="text-4xl sm:text-5xl leading-none text-outline-lime">{total.toLocaleString()}</span>
          <span className="text-2xl sm:text-3xl leading-none">total</span>
        </motion.div>
      </div>

      {/* Row 2: score % (pulses) + festive Wahoo */}
      <div className="flex items-center justify-center gap-3">
        {scorePercent != null && (
          <motion.div
            initial={{ scale: 1 }}
            animate={{ scale: [1, 1.5, 1, 1.5, 1] }}
            transition={{ duration: 1.8, times: [0, 0.25, 0.5, 0.75, 1], ease: "easeOut" }}
            className="text-4xl font-extrabold font-display text-[#8fb300] origin-center"
            data-testid="quiz-score">
            {scorePercent}%
          </motion.div>
        )}
        <div className="text-2xl sm:text-3xl font-extrabold font-display text-black" data-testid="quiz-cheer">
          🎉 Wahoo!
        </div>
      </div>

      <CoinStreakBanner celebrationsEnabled={soundOn} compact className="mt-2 max-w-md mx-auto justify-center text-center" />

      <AnimatePresence>
        {phase === "done" && (
          <motion.div
            ref={doneRef}
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }} data-testid="coin-done" className="scroll-mb-24">
            <p className="text-black font-semibold max-w-md mx-auto mt-2" data-testid="quiz-encouragement">
              You're awesome — keep going for even more coins!
            </p>
            <div className="flex items-center justify-center gap-2 mt-3 flex-wrap">
              {hasPrev && (
                <button data-testid="quiz-prev-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={() => onPrev && onPrev()}>
                  ← Prev
                </button>
              )}
              {onRetry && <button data-testid="quiz-retry-button" className="btn-secondary text-sm px-4 py-2 whitespace-nowrap" onClick={onRetry}>Redo</button>}
              {canAdvance && onContinue && (
                <button data-testid="quiz-continue-button" className="btn-primary text-sm px-4 py-2 whitespace-nowrap" onClick={onContinue}>
                  Next →
                </button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
